import ShopifyService from './ajax-service';

export const CART_EVENTS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  LOADING: 'LOADING',
  FINISHED: 'FINISHED',
}

class CartService {
  constructor() {
    this.cart = {
      id: null,
      subtotal: {
        amount: 0,
        currencyCode: window.TMW?.localization?.currencyCode
      },
      lines: [],
    };
    this.loading = false;
    this.observers = [];

    this.get();

    // For backwards compatibility we will listen for events
    // However, we should avoid using events and import/call methods on CartService directly
    document.addEventListener('cart:add', (e) => this.add(e.detail?.payload));
    document.addEventListener('cart:updateAttributes', (e) => this.updateAttributes(e.detail?.payload));
    this.mode = ShopifyService.name;
  }
 
  async callShopify(method, event, ...params) {
    const triggerToNotify =
      event === CART_EVENTS.UPDATE ? this._lastOpenTrigger : undefined;
    this.loading = true;
    this.notify(CART_EVENTS.LOADING);
    try {
      const cart = await ShopifyService[method](this.cart, ...params);
      this.validateCart(cart);
      if (cart) {
        this.cart = cart;
        
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart, method }
        }));
      }
      if (event) {
        this.notify(event, triggerToNotify);
      }
      
      return cart;
    } finally {
      this.loading = false;
      this.notify(CART_EVENTS.FINISHED);
    }
   
  }

  async get(silent = false) {
    return this.callShopify('get', silent ? null : CART_EVENTS.CREATE);
  }

  async add(lines, silent = false) {
    this._lastOpenTrigger =
      document.activeElement instanceof Element ? document.activeElement : null;
    return this.callShopify('add', silent ? null : CART_EVENTS.UPDATE, lines);
  }

  async remove(lines, silent = false) {
    return this.callShopify('remove', silent ? null : CART_EVENTS.UPDATE, lines);
  }

  async update(lines, silent = false) {
    return this.callShopify('update', silent ? null : CART_EVENTS.UPDATE, lines);
  }

  async updateAttributes(attributes, silent = true) {
    return this.callShopify('updateAttributes', silent ? null : CART_EVENTS.UPDATE, attributes);
  }
 


  async validateCart(cart) {
    if (!Array.isArray(cart?.items)) return;
  
    const engravingProductId = 
    window.TMW?.global_settings?.engravingProduct?.id;
    const embossingProductId = 
    window.TMW?.global_settings?.embossingProduct?.id;
  
    const actions = [];
  
    if (engravingProductId) {
      actions.push(this.validatePersonalizationType(cart, {
        productId: engravingProductId,
        parentProperty: 'Engraving',
        productFlag: '_EngravingProduct',
        typeLabel: 'engraving'
      }));
    }
  
    if (embossingProductId) {
      actions.push(this.validatePersonalizationType(cart, {
        productId: embossingProductId,
        parentProperty: 'Embossing',
        productFlag: '_EmbossingProduct',
        typeLabel: 'embossing'
      }));
    }
  
    await Promise.all(actions);
  }
  
  async validatePersonalizationType(cart, 
    { productId, parentProperty, productFlag, typeLabel }) {
    const variantId = productId.split('/').pop();
  
    let addonProductCount = 0;
    let parentProductCount = 0;
    let addonLine = null;
  
    for (const item of cart.items) {
      const props = item.properties || {};
  
      if (props[productFlag] && item.variant_id.toString() === variantId) {
        addonProductCount += item.quantity;
        addonLine = item;
      } else if (props[parentProperty]) {
        parentProductCount += item.quantity;
      }
    }
  
    const delta = parentProductCount - addonProductCount;
  
    if (delta > 0) {
      // eslint-disable-next-line no-console
      console.info(`🟡 ${addonLine ? 'Updating' : 'Adding'} ${typeLabel} product with quantity ${addonProductCount + delta}`);
      if (addonLine) {
        await this.update([{
          id: addonLine.variant_id,
          quantity: addonProductCount + delta,
          properties: addonLine.properties,
          key: addonLine.key
        }]);
      } else {
        await this.add([{
          merchandiseId: productId,
          quantity: delta,
          attributes: [
            { key: productFlag, value: 'true' }
          ]
        }]);
      }
    } else if (delta < 0 && addonLine) {
      const newQuantity = addonProductCount + delta;
      if (newQuantity <= 0) {
        // eslint-disable-next-line no-console
        console.info(`🔴 removing ${typeLabel} product`);
        await this.remove([{
          id: addonLine.variant_id,
          quantity: addonLine.quantity,
          properties: addonLine.properties,
          key: addonLine.key
        }]);
      } else {
        // eslint-disable-next-line no-console
        console.info(`🟠 updating ${typeLabel} product to ${newQuantity}`);
        await this.update([{
          id: addonLine.variant_id,
          quantity: newQuantity,
          properties: addonLine.properties,
          key: addonLine.key
        }]);
      }
    } else {
      // eslint-disable-next-line no-console
      console.info(`✅ ${typeLabel} products correct`);
    }
  }
  
  notify(event, data) {
    this.observers.forEach((o) => o.call(o, event, data));
  }

  addListener(observer) {
    this.observers.push(observer);
  }

  removeListener(observer) {
    this.observers = this.observers.filter((o) => o !== observer);
  }
}

const CartServiceSingleton = new CartService();
export default CartServiceSingleton;
